import {ClientFunctionUtil} from '@mintdata-wsp/common/controllers/spreadsheet/functions/ClientFunctionUtil';
import {FunctionFeature, SheetType} from '@mintdata/mint-kit/api/functions/SheetAPI';
import {GUID} from '@mintdata/mint-kit/api/models/GUID';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import bind from 'bind-decorator';

export class FunctionArgType
{
    constructor(public readonly type:Type | SheetType | null)
    {
    }

    public get typeName():string
    {
        return (this.type) ? this.type.toString() : '';
    }

    toString()
    {
        return `${this.typeName}`;
    }
}

export class FunctionArg
{
    constructor(public readonly name:string,
                public readonly type:FunctionArgType,
                public readonly title:string,
                public readonly tip:string,
                public readonly optional:boolean = false,
                public readonly repeatable:boolean = false)
    {
    }

    public get typeName():string
    {
        return (this.type) ? this.type.toString() : '';
    }

    toString()
    {
        return `${this.name}:${this.typeName}`;
    }
}

export class FunctionReturn
{
    constructor(public readonly type:FunctionArgType,
                public readonly title:string,
                public readonly tip:string)
    {
    }
}

export type FunctionId = GUID;
export type FunctionNamespace = string;
export type FunctionName = string;
export type FunctionAlias = string;
export type FunctionQualifiedName = string; // FunctionNamespace '.' FunctionName

export type FunctionContainerCtor = (new () => IFunctionContainer);

export interface IFunctionContainer
{
    namespace:FunctionNamespace;
}

export type AFunctionInvoker = (id:FunctionId, args:any[], javaEvalContext?:any) => Promise<any>;

export class ClientFunction
{
    constructor(private readonly _invoker:AFunctionInvoker,
                public readonly id:FunctionId,
                public readonly alias:FunctionAlias,
                public readonly example:string,
                public readonly lazyOnly:boolean = false,
                public readonly namespace:FunctionNamespace,
                public readonly name:FunctionName,
                public readonly title:string,
                public readonly tip:string,
                public readonly icon:string,
                public readonly usesFeatures:FunctionFeature[],
                public readonly args:FunctionArg[],
                public readonly returns:FunctionReturn = null)
    {
    }

    public get isUsingSheetAPI():boolean
    {
        return this.usesFeatures && this.usesFeatures.length > 0;
    }

    public get isArrayFormula():boolean
    {
        return this._hasFeature(FunctionFeature.ArrayFormula);
    }

    public get isModifiesSpreadsheet():boolean
    {
        return this._hasFeature(FunctionFeature.ModifiesSpreadsheet);
    }

    public get isUsingTimers():boolean
    {
        return this._hasFeature(FunctionFeature.UsesTimers);
    }

    @bind
    private _hasFeature(feature:FunctionFeature)
    {
        if( !this.usesFeatures || this.usesFeatures.length === 0 )
            return false;

        return !!this.usesFeatures.find((f:FunctionFeature) => (f === feature));
    }

    @bind
    public getQualifiedName():FunctionQualifiedName
    {
        if( this.alias )
        {
            return this.alias;
        }
        return `${this.namespace}.${this.name}`;
    }

    @bind
    public isNumArgsEqualTo(numArgsExpected:number):boolean
    {
        return this.args.length === numArgsExpected;
    }

    @bind
    public hasMatchingSignature(argTypes:(Type | SheetType)[], returnType:Type | SheetType = null):boolean
    {
        return ClientFunctionUtil.hasMatchingSignature(this, argTypes, returnType);
    }

    @bind
    public invoke(args:any[], javaEvalContext?:any):Promise<any>
    {
        return this._invoker(this.id, args, javaEvalContext);
    }
}
